import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import moment, { duration } from 'moment'
import { useNavigate, useLocation } from 'react-router-dom'

import { deleteCompanies, getCompany, getFilteredCompanies } from 'app/actions/company'
import { PRECISE_TIME_FORMAT, TIME_FORMAT } from 'app/constants/date'
import { CompanyType } from 'app/constants/companyType'
import { ROUTES } from 'app/constants/routes'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { formatExtendedDuration } from 'app/common/dateHelper'
import { getCompanyTypeName } from 'app/common/converters/companyTypeToTextConverter'
import { determineSubscriptionDate } from 'app/common/suspensionHelper'
import { isAdmin } from 'app/services/local/roleService'
import { CompanyFilterDropdown } from 'app/components/common/companyFilterDropdown'
import { CompanyFilterResult } from 'app/models/company/companyFilter'
import { Package } from 'app/components/common/package'
import { ROLES_KEY, fetchRoles } from 'app/containers/administration/roleQueries'

import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon } from '@/modules/shared/components'
import { Pagination } from '@/modules/shared/components/Pagination'
import { useAdminPagingStore } from '@/modules/shared/stores/adminPagingStore'
import {
  clearAdminSelection,
  setAdminSelectedCount,
  subscribeToAdminRemove,
} from '@/modules/shared/stores/adminSelectionStore'
import { toast } from '@/lib/toasts/api'
import { Button } from '@/ui/button'
import { AdminColumn, AdminTable } from '../shared/AdminTable'
import { formatSize } from '../shared/formatSize'
import { AddCompanyForm } from './components/AddCompanyForm'
import EditCompanyModal from '@/modules/addCompany/components/EditCompanyModal'
import ManualCompanyModal from '@/modules/addCompany/components/ManualCompanyModal'
import { AddServerCompanyModal } from '@/modules/addCompany/components/AddServerCompanyModal'
import { useQueryAllServers } from '@/lib/api/server/serverHooks'
import { useUser } from '@/modules/shared/context/UserContext'
import { DatabaseBackupUpload } from '@/modules/databases/components/DatabaseBackupUpload'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon, faClose } from '@/lib/fa'
import AddUserColor from '@/assets/icons_new/AddUserColor.svg'
import { DATABASES_ROUTES } from '@/modules/databases/routes-typed'
import { USERS_ROUTES } from '@/modules/users/routes-typed'
import { ImportKeyForm } from './components/ImportKeyForm'
import { useMutationMoveCompanyToServiceAccount } from '@/modules/shared/query-client/useServiceAccountAdmin'

/** Wersja schematu, powyżej której baza jest aktualna - przeniesione ze starej zakładki. */
const LAST_DATABASE_VERSION = '202402201247005'

/** Serwer wymaga wszystkich tablic filtra, także pustych. */
const EMPTY_FILTERS: CompanyFilterResult = {
  searchValue: '',
  companySettlementTypes: [],
  companyTypes: [],
  packageOptions: [],
  synchronizationOptions: [],
  packageProposalOptions: [],
  subscriptionExpirationDateOptions: [],
  suspendedOptions: [],
  subcompanies: false,
}

type Props = {
  currentCompanyId?: string
  currentCompany?: CompanyDetailsModel
  // Osadzone w panelu konta usługowego: bez powrotu do panelu admina i zaznaczania
  // (usuwanie firm jest tylko w panelu admina), z przejściem do firmy.
  embedded?: boolean
  readOnly?: boolean
}

const AdminCompaniesPageContent: React.FC<Props> = ({
  currentCompanyId,
  currentCompany: currentCompanyProp,
  embedded = false,
  readOnly = false,
}) => {
  const { t } = useLocalize()
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedGroup, resetAll } = useTable()

  const inTenant = currentCompanyId != null
  const paging = useAdminPagingStore((s) =>
    inTenant ? s.tenantCompanies : s.companies,
  )
  const setPaging = useAdminPagingStore((s) =>
    inTenant ? s.setTenantCompanyPaging : s.setCompanyPaging,
  )

  const params = new URLSearchParams(location.search)
  const serverId = Number(params.get('filterByServer') ?? 0)
  const databaseId = Number(params.get('filterByDatabase') ?? 0)
  const hasUrlFilter = params.has('filterByServer') || params.has('filterByDatabase')

  const [page, setPage] = React.useState(
    embedded ? 1 : Math.round((paging.skip ?? 0) / (paging.take || 10)) + 1,
  )
  // Konto usługowe ma kilka-kilkanaście firm: cała lista naraz, bez stronicowania.
  const [pageSize, setPageSize] = React.useState(
    embedded ? 1000 : paging.take || 10,
  )
  const [orderBy, setOrderBy] = React.useState(paging.orderBy ?? 'createdDate')
  const [descending, setDescending] = React.useState(paging.descending ?? true)
  // Filtry panelu admina (wspólny store) nie mogą po cichu zawężać osadzonej tabeli.
  const [filters, setFilters] = React.useState<CompanyFilterResult>(
    embedded ? EMPTY_FILTERS : paging.filters,
  )
  // Formularz klucza sprzętowego potrzebuje listy ról do przypisania.
  const { data: roles = [] } = useQuery({
    queryKey: ROLES_KEY,
    queryFn: fetchRoles,
  })

  const [addOpen, setAddOpen] = React.useState(false)
  const [manualOpen, setManualOpen] = React.useState(false)
  // Konto usługowe nie ma własnego serwera: import z istniejącej bazy na każdy serwer,
  // do którego użytkownik ma dostęp.
  const [fromDatabaseServer, setFromDatabaseServer] = React.useState<{
    id: number
    name: string
  } | null>(null)
  const { getServerAccess } = useUser()
  const { data: allServers } = useQueryAllServers(embedded && !readOnly && isAdmin())
  const importServers = (
    embedded && !readOnly ? (isAdmin() ? (allServers ?? []) : getServerAccess()) : []
  ).map((server) => ({ id: server.id, name: server.name || server.address }))
  const [backupOpen, setBackupOpen] = React.useState(false)
  const [search, setSearch] = React.useState(filters?.searchValue ?? '')
  const [editedCompanyId, setEditedCompanyId] = React.useState<string | null>(
    null,
  )
  const [keyUserOpen, setKeyUserOpen] = React.useState(false)

  const mergedFilters: CompanyFilterResult = {
    ...(filters ?? ({} as CompanyFilterResult)),
    serverId,
    databaseId,
  }

  const query = useQuery({
    queryKey: [
      'admin',
      'companies',
      currentCompanyId ?? null,
      pageSize,
      page,
      orderBy,
      descending,
      mergedFilters,
    ],
    queryFn: () =>
      getFilteredCompanies(
        currentCompanyId,
        pageSize,
        pageSize * (page - 1),
        orderBy,
        descending,
        mergedFilters,
      ),
  })

  // Firma nadrzędna daje przycisk „wstecz" nad listą, tak jak w starej zakładce.
  // W panelu konta usługowego nie jest potrzebna, a członek konta nie ma prawa jej
  // odczytać - 404 przerzucało go na stronę błędu.
  const parentQuery = useQuery({
    queryKey: ['admin', 'companies', 'parent', currentCompanyId ?? null],
    queryFn: () => getCompany(currentCompanyId, true),
    enabled: inTenant && !embedded,
  })

  const companies: CompanyDetailsModel[] = query.data?.companies ?? []
  const total: number = query.data?.companiesCount ?? 0
  const activeDriversCount: number | string = query.data?.activeDriversCount ?? '…'
  const parentCompany = inTenant ? parentQuery.data : null
  const currentCompany = currentCompanyProp ?? parentCompany ?? undefined

  // Ustawienia listy przeżywają odmontowanie zakładki - store trzyma je jak dawniej.
  React.useEffect(() => {
    if (embedded) return
    setPaging({
      descending,
      orderBy,
      filters,
      skip: pageSize * (page - 1),
      take: pageSize,
    } as any)
  }, [embedded, descending, orderBy, filters, page, pageSize, setPaging])

  // Osadzona tabela ma samo pole nazwy zamiast panelu filtrów obsługi.
  React.useEffect(() => {
    if (!embedded || search === (filters?.searchValue ?? '')) return undefined
    const timer = setTimeout(() => {
      setFilters({ ...EMPTY_FILTERS, searchValue: search })
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [embedded, search, filters?.searchValue])

  // Firmy podpięte pod firmę konta usługowego zamiast pod samo konto.
  const misnestedAccounts = React.useMemo(() => {
    const map = new Map<string, CompanyDetailsModel>()
    for (const parent of companies) {
      if (parent.type === CompanyType.SERVICE) continue
      if (parent.tenant?.type !== CompanyType.SERVICE) continue
      for (const child of parent.childCompanies ?? []) map.set(child.id, parent.tenant)
    }
    return map
  }, [companies])

  const moveToServiceAccount = useMutationMoveCompanyToServiceAccount()
  const handleMoveToServiceAccount = async (company: CompanyDetailsModel) => {
    try {
      await moveToServiceAccount.mutateAsync({ companyId: company.id })
      toast.success({
        title: t('administrationPanel-companies.moved-to-service-account'),
        text: company.companyName ?? '',
      })
      await query.refetch()
    } catch (err: any) {
      toast.error({
        title: t('administrationPanel-companies.move-to-service-account-error'),
        text: err?.response?.data?.code ?? err?.message ?? '',
      })
    }
  }

  const selectedCount = selectedGroup?.ids.length ?? 0
  React.useEffect(() => {
    setAdminSelectedCount(selectedCount)
  }, [selectedCount])

  const selectedRef = React.useRef<Array<string | number>>([])
  selectedRef.current = selectedGroup?.ids ?? []

  React.useEffect(() => {
    const unsubscribe = subscribeToAdminRemove(async () => {
      const ids = selectedRef.current.map(String)
      if (ids.length === 0) return
      const deleted = await deleteCompanies(ids)
      if (deleted.length > 0) {
        toast.success({
          title: t('administrationPanel-companies.companies-deleted-title'),
          text: t('administrationPanel-companies.companies-deleted'),
        })
        resetAll()
        await query.refetch()
      }
      clearAdminSelection()
    })
    return () => {
      unsubscribe()
      clearAdminSelection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Osadzona tabela nie wyprowadza do panelu admina: edycja w oknie, reszta
  // na ekrany firmy w aplikacji.
  const openCompany = (company: CompanyDetailsModel) =>
    navigate(`/et/company/${company.id}/dashboard`)

  const goToEdit = (company: CompanyDetailsModel) =>
    embedded
      ? setEditedCompanyId(company.id)
      : navigate(
          ROUTES.AdministrationPanel.EditCompany.replace(
            ':companyId',
            company.id,
          ).replace(':tab', 'company'),
        )

  const goToChildren = (company: CompanyDetailsModel) => {
    if (embedded) {
      navigate(USERS_ROUTES.usersList.buildPath({ companyId: company.id }))
      return
    }
    const url =
      company.type === CompanyType.SERVICE
        ? ROUTES.AdministrationPanel.CompanyTab.replace(
            ':companyId',
            company.id,
          ).replace(':tab', 'company')
        : ROUTES.AdministrationPanel.CompanyTab.replace(
            ':companyId',
            company.id,
          ).replace(':tab', 'user')
    navigate(url)
  }

  const goToDatabases = (company: CompanyDetailsModel) => {
    if (embedded) {
      navigate(DATABASES_ROUTES.databases.buildPath({ companyId: company.id }))
      return
    }
    const base = currentCompanyId
      ? ROUTES.AdministrationPanel.CompanyTab.replace(
          ':companyId',
          currentCompanyId,
        )
      : ROUTES.AdministrationPanel.NoCompanyTab
    navigate(
      base.replace(':tab', `database?filterByDatabase=${company.databaseId}`),
    )
  }

  const statusCell = (company: CompanyDetailsModel) => {
    if (company.accountingOnly) {
      return (
        <AdminIcon
          name="accounting"
          title={t('administrationPanel-companies.accounting')}
        />
      )
    }
    if (company.type === CompanyType.NORMAL) {
      return company.isValid ? (
        <AdminIcon
          name="connected"
          title={t('administrationPanel-companies.database-connected')}
        />
      ) : (
        <AdminIcon
          name="not-connected"
          title={
            t('administrationPanel-companies.database-not-connected') +
            (company.lastErrorMessage ? `: ${company.lastErrorMessage}` : '')
          }
        />
      )
    }

    const children = company.childCompanies ?? []
    if (children.length === 0) {
      return (
        <AdminIcon
          name="unknown"
          title={t('administrationPanel-companies.children-empty')}
        />
      )
    }
    return children.every((child) => child.isValid) ? (
      <AdminIcon
        name="connected"
        title={t('administrationPanel-companies.children-all-connected')}
      />
    ) : (
      <AdminIcon
        name="processing"
        title={t('administrationPanel-companies.children-some-connected')}
      />
    )
  }

  const columns: Array<AdminColumn<CompanyDetailsModel>> = []

  columns.push({
    key: 'status',
    header: t('administrationPanel-companies.status'),
    width: 70,
    align: 'center',
    render: statusCell,
  })

  if (isAdmin()) {
    columns.push({
      key: 'typeFormatted',
      header: t('administrationPanel-companies.company-type'),
      width: 150,
      render: (company) => (
        <span className="tw-flex tw-items-center tw-gap-2">
          {company.isPro && (
            <span
              className="tw-rounded tw-px-1 tw-text-[11px] tw-font-medium tw-text-white"
              style={{
                backgroundColor: company.proFeatureDisabled
                  ? '#888'
                  : company.hasKeyProFeature
                    ? '#7F0000'
                    : '#007F0E',
              }}
            >
              PRO
            </span>
          )}
          <span className="tw-flex tw-flex-col">
            <span>
              {t(getCompanyTypeName(company.type, company.accountingOnly))}
            </span>
            {company.multipleKeys && !company.multipleLogsFromKeys && (
              <span className="tw-h-0.5 tw-w-full tw-bg-[#0072FF]" />
            )}
            {company.multipleLogsFromKeys && (
              <span className="tw-h-0.5 tw-w-full tw-bg-[#FF006E]" />
            )}
          </span>
        </span>
      ),
    })
  }

  columns.push({
    key: 'companyName',
    header: t('administrationPanel-companies.company-name'),
    sortable: true,
  })

  if (!isAdmin()) {
    columns.push({
      key: 'serverNamesFormatted',
      header: t('administrationPanel-companies.server'),
      render: (company) => {
        const value =
          company.serverNames != null && company.serverNames.length > 0
            ? company.serverNames
                .filter((name, index, all) => all.indexOf(name) === index)
                .join(', ')
            : company.serverName
              ? company.serverName
              : '-'
        return <span title={value}>{value}</span>
      },
    })
  }

  columns.push({
    key: 'subscriptionExpirationDate',
    header: t('administrationPanel-companies.subscriptionDate'),
    width: 140,
    sortable: true,
    render: (company) => {
      if (company.suspended) {
        return (
          <span className="tw-rounded tw-bg-[#FD341F] tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white">
            {t('administrationPanel-companies.suspended')}
          </span>
        )
      }
      const date = determineSubscriptionDate(
        company.monthlySubscription,
        company.paid,
        company.paymentDaysOverride != null
          ? company.paymentDaysOverride
          : company.paymentDays,
        company.subscriptionExpirationDate,
        company.monthlySubscriptionDateOverride,
      )
      return date ? moment(date).format(TIME_FORMAT) : '-'
    },
  })

  if (isAdmin()) {
    columns.push({
      key: 'databaseVersion',
      header: t('administrationPanel-databases.version'),
      width: 140,
      align: 'right',
      sortable: true,
      render: (company) => (
        <span
          style={{
            color:
              company.databaseVersion != null &&
              company.databaseVersion !== LAST_DATABASE_VERSION
                ? '#f00'
                : undefined,
          }}
        >
          {company.databaseVersion}
        </span>
      ),
    })
  }

  columns.push({
    key: 'currentDriversCount',
    header: t('administrationPanel-companies.current-drivers-count'),
    width: 80,
    align: 'center',
    render: (company) => (
      <span className="tw-font-medium tw-text-[#EE59FF]">
        {company.currentDriversCount ? company.currentDriversCount : '?'}
      </span>
    ),
  })

  if (isAdmin()) {
    columns.push({
      key: 'driversCount',
      header: t('administrationPanel-companies.package-title'),
      width: 240,
      render: (company) => (
        <span className="tw-flex tw-items-center tw-gap-2">
          <span>{company.driversCount ? company.driversCount : '?'}</span>
          <Package
            isHeader={false}
            enableTooltip={false}
            translate={t}
            driversCount={company.proDriversCount}
            predefinedPackage={company.package}
            packageMultiplier={company.packageMultiplier}
            activeDriversLimits={company.activeDriversLimits}
            additionalPaidDrivers={company.additionalPaidDrivers}
            packageAmount={company.packageAmount}
          />
        </span>
      ),
    })
    columns.push({
      key: 'lastSynchronizationDate',
      header: t('administrationPanel-companies.last-synchronization-date'),
      width: 230,
      sortable: true,
      render: (company) =>
        company.lastSynchronizationDate ? (
          <span className="tw-flex tw-justify-between tw-gap-2">
            <span className="tw-flex tw-items-center tw-gap-1">
              {moment.utc(company.lastSynchronizationDate).local().format(TIME_FORMAT)}
              {company.synchronizationSource === 1 && (
                <span className="tw-rounded tw-bg-[#7975FB] tw-px-1 tw-text-[11px] tw-font-medium tw-text-white">
                  U
                </span>
              )}
            </span>
            <span className="tw-italic">
              {formatExtendedDuration(
                duration(moment().diff(moment(company.lastSynchronizationDate))),
                true,
              )}
            </span>
          </span>
        ) : (
          '?'
        ),
    })
    columns.push({
      key: 'size',
      header: t('administrationPanel-companies.size'),
      width: 110,
      align: 'right',
      sortable: true,
      render: (company) => formatSize(company.size),
    })
  } else {
    columns.push({
      key: 'driversCount',
      header: t('administrationPanel-databases.drivers-count'),
      width: 100,
      align: 'right',
      render: (company) =>
        company.proDriversCount ? company.proDriversCount : '?',
    })
    columns.push({
      key: 'createdBy',
      header: t('auditFields.created-by'),
      sortable: true,
    })
  }

  columns.push({
    key: 'createdDate',
    header: t('auditFields.created-date'),
    width: 150,
    sortable: true,
    render: (company) => (
      <span
        style={{
          color: company.deletedDate ? '#f00' : undefined,
          fontWeight: company.deletedDate ? 600 : 400,
        }}
      >
        {company.deletedDate
          ? moment.utc(company.deletedDate).local().format(PRECISE_TIME_FORMAT)
          : company.createdDate
            ? moment.utc(company.createdDate).local().format(PRECISE_TIME_FORMAT)
            : ''}
      </span>
    ),
  })

  columns.push({
    key: 'databasesCount',
    header: t('administrationPanel-companies.database'),
    width: 50,
    align: 'center',
    render: (company) =>
      company.type === CompanyType.NORMAL && !company.accountingOnly ? (
        <AdminIcon
          name="arrow-right"
          title={t('administrationPanel-companies.go-to-databases')}
          onClick={(event) => {
            event.stopPropagation()
            goToDatabases(company)
          }}
        />
      ) : null,
  })

  // Abonament, wersja bazy, pakiet i synchronizacja to dane dla obsługi, nie dla
  // właściciela konta.
  const EMBEDDED_HIDDEN = [
    'subscriptionExpirationDate',
    'databaseVersion',
    'currentDriversCount',
    'driversCount',
    'lastSynchronizationDate',
  ]
  if (embedded) {
    columns.splice(
      0,
      columns.length,
      ...columns.filter((column) => !EMBEDDED_HIDDEN.includes(column.key)),
    )
    columns.push({
      key: 'open',
      header: '',
      width: 58,
      align: 'center',
      render: (company) =>
        company.type === CompanyType.NORMAL && !company.accountingOnly ? (
          <AdminIcon
            name="dashboard"
            title={t('serviceAccount.open')}
            onClick={(event) => {
              event.stopPropagation()
              openCompany(company)
            }}
          />
        ) : null,
    })
  }

  if (!readOnly && !embedded && isAdmin() && misnestedAccounts.size > 0) {
    columns.push({
      key: 'move-to-service-account',
      header: '',
      width: 58,
      align: 'center',
      render: (company) => {
        const account = misnestedAccounts.get(company.id)
        return account ? (
          <AdminIcon
            name="company-group"
            title={`${t('administrationPanel-companies.move-to-service-account')}: ${account.companyName ?? ''}`}
            onClick={(event) => {
              event.stopPropagation()
              if (!moveToServiceAccount.isPending) handleMoveToServiceAccount(company)
            }}
          />
        ) : null
      },
    })
  }

  if (!readOnly) {
    columns.push({
      key: 'edit',
      header: '',
      width: 58,
      align: 'center',
      render: (company) => (
        <AdminIcon
          name="edit"
          title={t('operation.edit')}
          onClick={(event) => {
            event.stopPropagation()
            goToEdit(company)
          }}
        />
      ),
    })

    columns.push({
      key: 'child-company',
      header: '',
      width: 58,
      align: 'center',
      render: (company) => (
        <AdminIcon
          name="child"
          title={t('administrationPanel-companies.edit-child-company')}
          onClick={(event) => {
            event.stopPropagation()
            goToChildren(company)
          }}
        />
      ),
    })
  }

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      {!embedded && parentCompany && (isAdmin() || parentCompany.tenant) && (
        <div className="tw-flex-shrink-0 tw-px-4 tw-pt-2">
          <Button
            variant="primary"
            mode="outline"
            size="sm"
            onClick={() =>
              navigate(
                parentCompany.tenant != null
                  ? ROUTES.AdministrationPanel.CompanyTab.replace(
                      ':companyId',
                      parentCompany.tenant.id,
                    ).replace(':tab', 'company')
                  : ROUTES.AdministrationPanel.NoCompanyTab.replace(
                      ':tab',
                      'company',
                    ),
              )
            }
          >
            <AdminIcon name="arrow-left" className="tw-h-4 tw-w-4" />
            {t('administrationPanel-companies.go-back')}
          </Button>
        </div>
      )}

      {/* W koncie usługowym firmy dodaje się tak jak w Kontrahentach: ręcznie albo z
          kopii zapasowej; obie ścieżki podpinają nową firmę pod konto. */}
      {embedded && !readOnly && currentCompanyId && (
        <div className="tw-flex tw-flex-shrink-0 tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-base-300 tw-px-4 tw-py-1.5">
          {[
            { label: t('addCompany.manualCreate'), onClick: () => setManualOpen(true) },
            { label: t('databases.from-backup-tab'), onClick: () => setBackupOpen(true) },
            ...importServers.map((server) => ({
              label:
                importServers.length > 1
                  ? `${t('addCompany.addFromDatabase')} (${server.name})`
                  : t('addCompany.addFromDatabase'),
              onClick: () => setFromDatabaseServer(server),
            })),
          ].map((action) => (
            <button
              key={action.label}
              type="button"
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90"
              onClick={action.onClick}
            >
              <img src={AddUserColor} alt="" />
              <span className="tw-text-sm tw-font-medium tw-text-primary">
                {action.label}
              </span>
            </button>
          ))}
        </div>
      )}

      {!embedded && !readOnly && (
        <div className="tw-flex-shrink-0 tw-border-b tw-border-base-300">
          <button
            type="button"
            className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-font-medium"
            onClick={() => setAddOpen((open) => !open)}
          >
            <AdminIcon name={addOpen ? 'arrow-up' : 'arrow-down'} />
            {t('administrationPanel-companies.add-company')}
          </button>
          {addOpen && (
            <AddCompanyForm
              currentCompanyId={currentCompanyId}
              currentCompany={currentCompany}
              existingCompanies={companies}
              onAdded={async () => {
                await query.refetch()
              }}
            />
          )}
        </div>
      )}

      {!embedded && currentCompanyId && isAdmin() && currentCompany && (
        <div className="tw-flex-shrink-0 tw-border-b tw-border-base-300">
          <button
            type="button"
            className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-font-medium"
            onClick={() => setKeyUserOpen((open) => !open)}
          >
            <AdminIcon name={keyUserOpen ? 'arrow-up' : 'arrow-down'} />
            {t('administrationPanel-users.add-key-user')}
          </button>
          {keyUserOpen && (
            <ImportKeyForm
              company={currentCompany}
              companyId={currentCompanyId}
              roles={roles as RolePermissionModel[]}
              onUserAdded={async () => {
                setKeyUserOpen(false)
                await query.refetch()
              }}
            />
          )}
        </div>
      )}

      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-4 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
        <AdminIcon name="search" />
        <div className="tw-min-w-0 tw-flex-1">
          {embedded ? (
            <input
              className="tw-w-full tw-border-0 tw-bg-transparent tw-py-2 tw-text-sm tw-outline-none"
              placeholder={t('serviceAccount.search-company')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          ) : (
            <CompanyFilterDropdown
              caret=""
              initial={filters}
              findPlaceholder=""
              onSelected={(result: CompanyFilterResult) => {
                setFilters(result)
                setPage(1)
              }}
              translate={t}
            />
          )}
        </div>
        {hasUrlFilter && (
          <Button
            variant="primary"
            mode="outline"
            size="sm"
            onClick={() => navigate(location.pathname)}
          >
            <AdminIcon name="close" className="tw-h-4 tw-w-4" />
            {t('administrationPanel-companies.remove-filters')}
          </Button>
        )}
        {(isAdmin() || embedded) && (
          <span
            className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 tw-text-sm"
            title={t('serviceAccount.active-drivers-tip')}
          >
            <AdminIcon name="people" />
            {embedded
              ? t('serviceAccount.active-drivers', { count: String(activeDriversCount) })
              : activeDriversCount}
          </span>
        )}
      </div>

      <AdminTable
        idPrefix="company"
        rows={companies}
        columns={columns}
        rowId={(company) => company.id}
        selectable={!embedded}
        subRows={(company) => company.childCompanies ?? []}
        isLoading={query.isLoading}
        orderBy={orderBy}
        descending={descending}
        onSort={(key) => {
          setDescending(orderBy === key ? !descending : true)
          setOrderBy(key)
          setPage(1)
        }}
        onRowClick={embedded ? openCompany : undefined}
        onRowDoubleClick={embedded || readOnly ? undefined : goToEdit}
        footer={
          embedded ? undefined : (
            <div className="tw-flex-shrink-0 tw-border-t tw-border-base-300">
              <Pagination
                currentPage={page}
                totalItems={total}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
              />
            </div>
          )
        }
      />
      {embedded && currentCompanyId && (
        <ManualCompanyModal
          isOpen={manualOpen}
          companyId={currentCompanyId}
          close={() => {
            setManualOpen(false)
            query.refetch()
          }}
        />
      )}
      {embedded && currentCompanyId && fromDatabaseServer && (
        <AddServerCompanyModal
          isOpen
          close={() => {
            setFromDatabaseServer(null)
            query.refetch()
          }}
          serverId={fromDatabaseServer.id}
          serverName={fromDatabaseServer.name}
          tenantCompanyId={currentCompanyId}
          defaultUseExistingDatabase
        />
      )}
      {embedded && currentCompanyId && backupOpen && (
        <ModalLayout>
          <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[900px] tw-flex-col tw-rounded-lg tw-bg-white">
            <div className="tw-flex tw-justify-end tw-px-4 tw-pt-4">
              <FaIcon
                icon={faClose}
                className="tw-h-5 tw-w-5 tw-cursor-pointer"
                onClick={() => setBackupOpen(false)}
              />
            </div>
            <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-6">
              <DatabaseBackupUpload companyId={currentCompanyId} />
            </div>
          </div>
        </ModalLayout>
      )}
      <EditCompanyModal
        isOpen={editedCompanyId != null}
        editedCompanyId={editedCompanyId}
        close={() => {
          setEditedCompanyId(null)
          query.refetch()
        }}
      />
    </div>
  )
}

/**
 * Zakładka FIRMY panelu admina - funkcyjna, na react-query i `AdminTable`.
 *
 * Zastępuje `app/components/administration/companies.tsx` (react-table v6, klasa na
 * `ValidatorBase`). Stronicowanie, sortowanie i filtry idą na serwer; firmy serwisowe
 * rozwijają się do firm podrzędnych tymi samymi kolumnami, tak jak dawny `SubComponent`.
 */
export const AdminCompaniesPage: React.FC<Props> = (props) => (
  <TableProvider>
    <AdminCompaniesPageContent {...props} />
  </TableProvider>
)
